import { useRef } from 'react';
import FormItem from 'components/FormItem';
import { Input } from 'antd';
import SearchAddress from 'components/SearchAddress';
import AsyncSelect from 'components/Select/AsyncSelect';
import { US_COUNTRY_CODES } from 'components/PlaceAutoComplete/PlaceAutoComplete';
import FormattedAddress from 'components/FormattedAddress/FormattedAddress';

/**
 * Hook that returns an array of TabularList data items for address fields
 * This allows each address field to render as a separate row in TabularList
 * while maintaining the form structure and validation
 */
export const useAddressFieldsData = ({ form, addressKey = 'address', isEditing, addressValue, disabled = false }) => {
  const searchRef = useRef();

  if (!isEditing) {
    return [
      {
        title: 'Address',
        titleRequired: true,
        value: <FormattedAddress address={addressValue} />,
      },
    ];
  }

  return [
    {
      title: 'Street Address',
      titleRequired: true,
      value: (
        <FormItem
          name={[addressKey, 'street']}
          className="mb-0"
          rules={[{ required: true, message: 'Street address is required' }]}
        >
          <SearchAddress
            ref={searchRef}
            form={form}
            addressKey={addressKey}
            country={US_COUNTRY_CODES}
            onSelect={(location) => {
              form.setFieldValue(addressKey, {
                ...location,
                postalCode: location?.zipCode,
                street2: '',
                location: {
                  lat: location?.lat,
                  lon: location?.lng,
                },
              });
              form.validateFields([
                [addressKey, 'city'],
                [addressKey, 'state'],
                [addressKey, 'postalCode'],
              ]);
              searchRef.current?.update(location?.street || '');
            }}
            disabled={disabled}
          />
        </FormItem>
      ),
    },
    {
      title: 'Address Line 2',
      value: (
        <FormItem name={[addressKey, 'street2']} className="mb-0">
          <Input disabled={disabled} placeholder="Apartment, suite, etc. (optional)" />
        </FormItem>
      ),
    },
    {
      title: 'City',
      titleRequired: true,
      value: (
        <FormItem
          name={[addressKey, 'city']}
          className="mb-0"
          rules={[{ required: true, message: 'City is required' }]}
        >
          <Input disabled={disabled} />
        </FormItem>
      ),
    },
    {
      title: 'State',
      titleRequired: true,
      value: (
        <FormItem
          name={[addressKey, 'state']}
          className="mb-0"
          rules={[{ required: true, message: 'State is required' }]}
        >
          <AsyncSelect
            placeholder="Select..."
            ariaLabel="State"
            disabled={disabled}
            optionsApiUrl="/countries/states/US"
            getOptionLabel={(opt) => opt.name}
            getOptionValue={(opt) => opt.code}
            isOptionSelected={(option, [value]) => option?.code === value}
            onChangeFormatter={(v) => v?.code}
            isClearable={false}
          />
        </FormItem>
      ),
    },
    {
      title: 'Zip',
      titleRequired: true,
      value: (
        <FormItem
          name={[addressKey, 'postalCode']}
          className="mb-0"
          rules={[
            { required: true, message: 'Zip code is required' },
            {
              pattern: /^\d{5}(-\d{4})?$/,
              message: 'Please enter a valid US zip code',
            },
          ]}
        >
          <Input disabled={disabled} maxLength={10} />
        </FormItem>
      ),
    },
  ];
};
